import { type CSSProperties } from 'react';
import { ICON_SIZES, type IconSize } from '../../constants/iconSizes';

/** 丸枠の種類 */
export type CircleStyle = 'solid' | 'dashed';

export interface MaterialIconProps {
  /** Material Symbols アイコン名 */
  icon: string;
  /** アイコンサイズ（ICON_SIZES のキー） */
  size?: IconSize;
  /** アイコンの色 */
  color?: string;
  /** 丸枠で囲む場合の線種（solid: 実線, dashed: 破線） */
  circled?: CircleStyle;
  /**
   * true のとき、 アイコンを fontSize と同じサイズの正方形 box で中央寄せにラップする。
   * Material Symbols は icon 種別によって文字幅が 16〜24px と差があるので、
   * 縦に複数の異なるアイコンを並べた行 (圃場イベント、 防除リスト等) でラベル開始位置を揃えるために使用する。
   * `circled` 指定時は固定幅 box は不要 (circled の box が同サイズで揃うため) なので無視される。
   */
  fixedWidth?: boolean;
  /** 追加のスタイル */
  style?: CSSProperties;
  /** 追加のクラス名 */
  className?: string;
}

/** アイコンサイズに対応する丸枠のサイズ */
const CIRCLE_SIZES: Record<IconSize, string> = {
  xs: '20px',
  sm: '24px',
  md: '28px',
  lg: '30px',
  xl: '32px',
  xxl: '40px',
};

/**
 * Material Symbols アイコンコンポーネント
 * 一貫したアイコン表示のためのラッパー
 */
export function MaterialIcon({
  icon,
  size = 'md',
  color,
  circled,
  fixedWidth = false,
  style,
  className,
}: MaterialIconProps) {
  const fontSize = ICON_SIZES[size];

  // 防除（sprinkler）アイコンは噴霧方向を下向きに見せるため 180 度回転して描画する。
  const isPestControlIcon = icon === 'sprinkler';

  const iconElement = (
    <span
      className={`material-symbols-outlined${className ? ` ${className}` : ''}`}
      style={{
        fontSize,
        color,
        lineHeight: 1,
        ...(isPestControlIcon ? { transform: 'rotate(180deg)', display: 'inline-block' } : {}),
        ...(!circled && !fixedWidth ? style : {}),
      }}
    >
      {icon}
    </span>
  );

  if (!circled && fixedWidth) {
    // 固定幅 box: fontSize と同じ正方形領域でアイコンを中央寄せ。 異なるアイコン種別 (grain / psychiatry /
    // sprinkler / grass 等) の文字幅差を吸収し、 ラベル開始位置を揃える。
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: fontSize,
          height: fontSize,
          flexShrink: 0,
          ...style,
        }}
      >
        {iconElement}
      </span>
    );
  }

  if (!circled) {
    return iconElement;
  }

  const circleSize = CIRCLE_SIZES[size];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: circleSize,
        height: circleSize,
        borderRadius: '50%',
        border: `2px ${circled} ${color}`,
        ...style,
      }}
    >
      {iconElement}
    </div>
  );
}
